import React from 'react';
import { Image as ImageIcon, Archive, RotateCcw, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { getAsset } from '@/client/utils/assets';
import { EntityAsset } from '@/client/api/listing-management/entity.query';
import { truthy } from './helpers';
import { Gallery } from './Gallery';

/**
 * Media manager — lists a listing's real (DB) assets INCLUDING archived
 * (soft-deleted) ones, so staff can archive a photo and restore it. Archiving is
 * reversible; nothing is permanently deleted. `model_json` fallback images
 * aren't DB rows (no asid), so they render read-only when there are no real
 * assets. All mutations are confirmed by the page-level dialog.
 */
export const MediaManager: React.FC<{
  loading: boolean;
  assets: EntityAsset[];
  fallbackImages: string[];
  busyAsid: number | null;
  onDelete: (asid: number) => void;
  onRestore: (asid: number) => void;
}> = ({ loading, assets, fallbackImages, busyAsid, onDelete, onRestore }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (assets.length === 0) {
    // No DB assets — show any model-match photos read-only, else an empty state.
    if (fallbackImages.length > 0) {
      return (
        <div className="space-y-3">
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
            These are product-match photos (not uploaded assets), so they can't be
            archived or restored.
          </p>
          <Gallery images={fallbackImages} />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <ImageIcon className="h-8 w-8 text-gray-300" />
        <p className="text-sm text-gray-400">No media for this listing.</p>
      </div>
    );
  }

  const activeCount = assets.filter((a) => !truthy(a.deleted)).length;

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {activeCount} active · {assets.length - activeCount} archived
      </p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {assets.map((a) => {
          const deleted = truthy(a.deleted);
          const url = getAsset(a as any);
          const rowBusy = busyAsid === a.asid;
          return (
            <div
              key={a.asid}
              className={`group relative overflow-hidden rounded-xl border ${
                deleted ? 'border-amber-200' : 'border-gray-200'
              }`}
            >
              <div className="aspect-square w-full bg-gray-100">
                {url ? (
                  <img
                    src={url}
                    alt={a.name ?? `Asset ${a.asid}`}
                    className={`h-full w-full object-cover ${deleted ? 'opacity-40 grayscale' : ''}`}
                    onError={(ev) => {
                      (ev.currentTarget as HTMLImageElement).style.opacity = '0.2';
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ImageIcon className="h-6 w-6 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Badges */}
              <div className="absolute left-2 top-2 flex gap-1">
                {truthy(a.cover) && (
                  <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-medium text-white">
                    Cover
                  </span>
                )}
                {deleted && (
                  <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-medium text-white">
                    Archived
                  </span>
                )}
              </div>

              {/* Action */}
              <div className="flex items-center justify-between gap-2 px-2.5 py-2">
                <span className="truncate font-mono text-[11px] text-gray-400">#{a.asid}</span>
                {deleted ? (
                  <button
                    disabled={rowBusy}
                    onClick={() => onRestore(a.asid)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    title="Restore photo"
                  >
                    {rowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                    Restore
                  </button>
                ) : (
                  <button
                    disabled={rowBusy}
                    onClick={() => onDelete(a.asid)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                    title="Archive photo (reversible)"
                  >
                    {rowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                    Archive
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
